// A form field removed while it has focus never blurs: the browser moves focus
// to the body without firing blur or focusout on the field. Password managers
// close their inline menu on that blur, so Bitwarden's dropdown stayed on screen
// after a navigation, a wizard step or a closing dialog took its field away.
//
// This sends the events the browser skipped, to the field that was removed and
// only to it. Nothing still on the page loses focus, which a blur on navigation
// would have done to a list's search box on every keystroke (the search writes
// the URL).

const FIELD = "input, textarea, select";

export function installBlurRemovedFields(root: Document = document): () => void {
  let focused: Element | null = null;

  const onFocusIn = (event: FocusEvent) => {
    const target = event.target;
    focused = target instanceof Element && target.matches(FIELD) ? target : null;
  };
  // A real blur happened, so there is nothing to make up for.
  const onFocusOut = (event: FocusEvent) => {
    if (event.target === focused) focused = null;
  };

  const observer = new MutationObserver(() => {
    if (!focused || focused.isConnected) return;
    const field = focused;
    focused = null;
    field.dispatchEvent(new FocusEvent("blur"));
    field.dispatchEvent(new FocusEvent("focusout", { bubbles: true }));
  });

  root.addEventListener("focusin", onFocusIn);
  root.addEventListener("focusout", onFocusOut);
  observer.observe(root.body, { childList: true, subtree: true });

  return () => {
    root.removeEventListener("focusin", onFocusIn);
    root.removeEventListener("focusout", onFocusOut);
    observer.disconnect();
  };
}
